<template>
  <div class="pagination">
    <div v-if="counter > limit" class="pages" :class="{disabled}">
      <div class="btn btn-left" :class="{'btn-disabled': model === minimum}" @click="setPage(model - 1)">
        &lt;
      </div>
      <div v-if="!list.includes(minimum)" class="btn" :class="{ 'btn-active': model === 1 }" @click="setPage(1)">
        <span>1</span>
      </div>
      <PaginationInput v-if="dots_start" :current="model" :min="minimum" :max="maximum" @change="setPage" />
      <div v-for="n in list" :key="n" class="btn" :class="{ 'btn-active': n === model }" @click="setPage(n)">
        <span>{{ n }}</span>
      </div>
      <PaginationInput v-if="dots_end" :current="model" :min="minimum" :max="maximum" @change="setPage" />
      <div v-if="!list.includes(maximum)" class="btn" :class="{ 'btn-active': model === maximum }" @click="setPage(maximum)">
        <span>{{ maximum }}</span>
      </div>
      <div class="btn btn-right" :class="{'btn-disabled': model === maximum}" @click="setPage(model + 1)">
        &gt;
      </div>
    </div>
    <div v-if="summary && counter > limit && !short" class="summary" v-html="summaryText" />
  </div>
</template>

<script setup>
import { getPrettyNumber } from 'bambam-helper/enum.js';
import { Logger } from 'bambam-logger';
import { computed, nextTick, watch } from 'vue';
import PaginationInput from './PaginationInput.vue';

Logger.debug('DEPRECATED COMPONENT', 'Pagination.vue');

const range = (start, end) => Array.from({ length: (end - start) }, (v, k) => k + start);

const emit = defineEmits(['update:modelValue', 'input']);

const state = defineProps({
  limit: {
    type: Number,
    default: 100,
  },
  modelValue: {
    type: Number,
    default: 0,
  },
  counter: Number,
  submitAction: Function,
  disabled: Boolean,
  summary: Boolean,
  short: Boolean,
  gap: {
    type: Number,
    default: 2,
  },
  summaryTemplate: {
    type: String,
    default: 'Showing {from} to {to} of {counter} records',
  },
  max: Number,
});

const counter_max = computed(() => (state.max ? Math.min(state.max, state.counter) : state.counter));

const minimum = computed(() => 1);
const maximum = computed(() => Math.ceil(counter_max.value / state.limit));
const maximum_items = computed(() => Math.min(maximum.value, (state.gap * 2) + 1));

const setPage = async (value) => {
  if (state.disabled) {
    return;
  }
  if (value > maximum.value) {
    value = maximum.value;
  }

  if (value < minimum.value) {
    value = minimum.value;
  }

  value = (value - 1) * state.limit;

  if (value === state.modelValue) {
    return;
  }
  emit('input', value);
  emit('update:modelValue', value);
  if (state.submitAction) {
    await nextTick();
    state.submitAction();
  }
};
const model = computed({
  get() {
    return Math.ceil((state.modelValue + 1) / state.limit);
  },
  set(value) {
    setPage(value);
  },
});

const from = computed(() => state.modelValue + 1);
const to = computed(() => Math.min(state.modelValue + state.limit, counter_max.value));

const list = computed(() => {
  let start = minimum.value;
  let end = maximum.value;
  if (model.value - state.gap <= start) {
    end = maximum.value <= 7
      ? start + maximum_items.value - 1
      : start + maximum_items.value - 2;
  } else if (model.value + state.gap >= end) {
    start = maximum.value <= 7
      ? end - maximum_items.value + 1
      : end - maximum_items.value + 2;
  } else {
    start = model.value - 1;
    end = model.value + 1;
  }

  return range(start - 1 || 1, end + 2 > maximum.value ? maximum.value : end + 2);
});

const dots_start = computed(() => !list.value.includes(minimum.value + 1) && maximum.value >= 6);
const dots_end = computed(() => !list.value.includes(maximum.value - 1) && maximum.value > 7);

watch(() => state.counter, (v) => {
  if (state.modelValue && v < state.modelValue && v) {
    setPage(0);
  }
}, { immediate: true });

const summaryText = computed(() => state.summaryTemplate
  .replace('{to}', `<span>${getPrettyNumber(to.value)}</span>`)
  .replace('{from}', `<span>${getPrettyNumber(from.value)}</span>`)
  .replace('{counter}', `<span>${getPrettyNumber(state.counter)}</span>`));
</script>
